'use client';

import {
  ChangeEvent,
  MouseEvent,
  PropsWithChildren,
  useState,
  useRef,
  useEffect,
} from 'react';

import { HighlightedPopover, HighlightMemo, MemoIcon } from './parts';

import { useHighlight, useProfile } from '@/shared/hooks';
import { useUpdateHighlight } from '@/entities/article';
import { getPureText } from '@/shared/utils';

interface HighlightedSpanProps {
  highlightId: string;
  text: string;
  memo?: string;
}

const HighlightedSpan = ({
  children,
  highlightId,
  text,
  memo: savedMemo,
}: PropsWithChildren<HighlightedSpanProps>) => {
  const {
    id: activeHighlightId,
    setId: setActiveHighlightId,
    reset: resetActiveHighlightId,
  } = useHighlight();
  const isActiveMemo = !!(
    activeHighlightId && String(activeHighlightId) === highlightId
  );
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { profile } = useProfile();
  const { mutate: updateHighlight } = useUpdateHighlight();
  const [isMemoOpen, setIsMemoOpen] = useState(isActiveMemo);
  const defaultMemoString = savedMemo || '';
  const parsedMemo = decodeURIComponent(defaultMemoString);
  const parsedText = decodeURIComponent(text);

  const organizedText = getPureText(parsedText);

  const [memo, setMemo] = useState(parsedMemo);
  const spanRef = useRef<HTMLDivElement>(null);

  const openMemo = () => {
    setIsPopoverOpen(false);
    setActiveHighlightId(Number(highlightId));
  };

  const closeMemo = () => {
    setIsMemoOpen(false);
    setMemo(parsedMemo);
    resetActiveHighlightId();
  };

  const onMemoChange = (e: ChangeEvent<HTMLTextAreaElement>) =>
    setMemo(e.target.value);

  const onMemoSubmit = () => {
    const profileId = profile?.yozmitProfileId;
    if (!profileId) return;

    updateHighlight(
      { highlightId, profileId, memo },
      {
        onSuccess() {
          resetActiveHighlightId();
          setIsPopoverOpen(false);
          setIsMemoOpen(false);
        },
      }
    );
  };

  const openPopover = (e: MouseEvent<HTMLSpanElement>) => {
    if (isMemoOpen) return;

    const isMemoClicked = e.target instanceof SVGPathElement;

    if (!isMemoClicked) setIsPopoverOpen(true);
  };

  const closePopover = () => {
    if (isMemoOpen) return;
    setIsPopoverOpen(false);
    resetActiveHighlightId();
  };

  const isMemoExist = savedMemo && savedMemo.length > 0;

  useEffect(() => {
    setIsMemoOpen(isActiveMemo);
  }, [isActiveMemo, activeHighlightId]);

  return (
    <HighlightedPopover
      isOpen={isPopoverOpen}
      onClickOutside={closePopover}
      onMemoOpen={openMemo}
      highlightId={highlightId}
      text={organizedText}
      spanRef={spanRef}
    >
      <span
        id="highlight-wrapper"
        className="relative inline w-full"
        ref={spanRef}
      >
        <span
          className="cursor-pointer bg-w-yellow-300 hover:bg-w-yellow-500"
          style={{
            textAlign: 'inherit',
            color: 'inherit',
          }}
          onClick={openPopover}
        >
          {children}
        </span>
        {isMemoExist && (
          <button className="absolute bottom-4" onClick={openMemo}>
            <MemoIcon />
          </button>
        )}
        {isMemoOpen && (
          <HighlightMemo
            isMemoOpen={isMemoOpen}
            memo={memo}
            onMemoChange={onMemoChange}
            onMemoClose={closeMemo}
            onMemoSubmit={onMemoSubmit}
          />
        )}
      </span>
    </HighlightedPopover>
  );
};

export default HighlightedSpan;
